import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { UserPlus, UserMinus, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const FollowButton = ({ username, onFollowChange }) => {
  const { user, isAuthenticated } = useAuth();
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  // Don't show button if viewing own profile
  const isOwnProfile = user?.username === username;

  useEffect(() => {
    checkFollowStatus();
  }, [username, isAuthenticated]);

  const checkFollowStatus = async () => {
    if (!isAuthenticated || isOwnProfile) {
      setChecking(false);
      return;
    }

    try {
      setChecking(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/users/${username}/is-following`,
        { withCredentials: true }
      );
      setIsFollowing(response.data.is_following);
    } catch (error) {
      console.error('Error checking follow status:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleFollow = async () => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication required",
        description: "Please log in to follow Fountainheads",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);

      if (isFollowing) {
        // Unfollow
        await axios.delete(
          `${BACKEND_URL}/api/users/${username}/unfollow`,
          { withCredentials: true }
        );
        setIsFollowing(false);
        toast({
          title: "Unfollowed",
          description: `You unfollowed @${username}`
        });
        if (onFollowChange) onFollowChange(false);
      } else {
        // Follow
        await axios.post(
          `${BACKEND_URL}/api/users/${username}/follow`,
          {},
          { withCredentials: true }
        );
        setIsFollowing(true);
        toast({
          title: "Following!",
          description: `You're now following @${username}`
        });
        if (onFollowChange) onFollowChange(true);
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update follow status",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Don't render if viewing own profile
  if (isOwnProfile) {
    return null;
  }

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  if (checking) {
    return (
      <Button disabled variant="outline" className="min-w-[120px]">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        Loading...
      </Button>
    );
  }

  return (
    <Button
      onClick={handleFollow}
      disabled={loading}
      variant={isFollowing ? "outline" : "default"}
      className={isFollowing ? "" : "bg-gradient-to-r from-purple-600 to-pink-600"}
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          {isFollowing ? 'Unfollowing...' : 'Following...'}
        </>
      ) : isFollowing ? (
        <>
          <UserMinus className="w-4 h-4 mr-2" />
          Unfollow
        </>
      ) : (
        <>
          <UserPlus className="w-4 h-4 mr-2" />
          Follow
        </>
      )}
    </Button>
  );
};

export default FollowButton;
